import type { AllianceApplication } from '@kaeliux/shared';
import { Button } from './ui/Button';
import { RACE_LOGOS } from '../utils/raceAssets';

interface AllianceApplicationListProps {
    applications: AllianceApplication[];
    onRespond: (applicantId: string, accept: boolean) => void;
    busyApplicantId: string | null;
}

/** Officer/leader review queue. Oldest application first, as the server returns it. */
export default function AllianceApplicationList({
    applications,
    onRespond,
    busyApplicantId,
}: AllianceApplicationListProps) {
    if (applications.length === 0) {
        return (
            <p className="text-content-muted" data-testid="alliance-applications-empty">
                No pending applications.
            </p>
        );
    }

    return (
        <ul className="flex flex-col gap-3" data-testid="alliance-applications">
            {applications.map((application) => {
                const busy = busyApplicantId === application.userId;
                return (
                    <li
                        key={application.userId}
                        className="p-3 bg-surface-raised rounded-chip border border-space-700 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                        data-testid={`alliance-application-${application.displayName}`}
                    >
                        <div className="flex items-start gap-3 min-w-0">
                            <img
                                src={RACE_LOGOS[application.race]}
                                alt=""
                                className="w-8 h-8 rounded-full object-cover shrink-0"
                            />
                            <div className="min-w-0">
                                <p className="text-content-primary">{application.displayName}</p>
                                {application.message && (
                                    <p className="text-content-muted text-label break-words">{application.message}</p>
                                )}
                                <p className="text-content-muted text-micro">
                                    Applied {application.createdAt.toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                loading={busy}
                                onClick={() => onRespond(application.userId, true)}
                            >
                                Accept
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                loading={busy}
                                onClick={() => onRespond(application.userId, false)}
                            >
                                Reject
                            </Button>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
